import './Map.css'
import './MapFilters.css'
import 'leaflet/dist/leaflet.css'

import React, { useEffect, useId, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { isAdminRole } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'

import { useAppContext } from '@/context/app'
import { MachineCreateWizard } from '@/popups/machines/wizard/create/Main'
import { MachineName } from '../machine'
import { MainDialogPopup } from '@/popups/dialog/dialog'
import { Spinner } from '../spinner'

export const vehicleIconSize = 16

export const activeTime = 5 * 60 * 1000; // fiveMinutesInMs

interface MapRowMachine {
  id: string | undefined
  name: string | undefined
  internalNumber: string | undefined
  type: {
    manufacturer: string;
    category: string;
    model: string;
  }
}

interface MapRowUser {
  id: string | undefined
  username: string | undefined
}

interface MapRowstatus {
  fire: boolean | null
}

export interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
  dtTracker: string // ISO 8601 string representation of DtTracker (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  soc: number // SOC
  engineHours: number // MTH of engine
  ucid: string // User Card ID
  machine: MapRowMachine
  user: MapRowUser
  status: MapRowstatus
}

export interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

export function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}

export const MapPopupBody = ({ row, removeFromMap }: { row: MapRow, removeFromMap: (imei: string) => void }) => {
  const { services, session } = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()

  if (!session || !services) {
    return <Spinner />
  }

  return <div className='map-popup-wrap'>
    <div onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>
      <MachineName row={row.machine} />
    </div>
    {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => isAdminRole(session.user.role) && openModal((props) => MachineCreateWizard({
      ...props, services, session, machine: {
        imei: row.imei,
      }, onDone: (id: string) => {
        navigate(`/communal/machine/${id}`)
      }
    }))}>{`IMEI: ${row.imei}`}</p> : null}
    {/* {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null} */}
    <p>{session.translates.for('map.speed')}: {row.speed} km/h</p>
    <p>{session.translates.for('map.direction')}: {row.angle}°</p>
    {row.soc ? <p>{session.translates.for('map.soc')}: {row.soc}%</p> : null}
    {row.engineHours ? <p>{session.translates.for('map.engineHours')}: {row.engineHours}MTH</p> : null}
    <p>Fire: {row.status.fire === null ? '-' : row.status.fire ? 'Tak' : 'Nie'}</p>

    {row.user?.id ? <>
      <p onClick={() => {
        navigate(`/communal/statistics/users?user=${row.user.id}`)
      }}>{session.translates.for('map.user')}: {row.user.username}
      </p>
    </> : row.ucid ? <p onClick={() => {
      navigate(`/communal/cards/manager?search=${row.ucid}`)
    }}>{session.translates.for('map.ucid')}:<br /> {row.ucid}</p> : null}

    <p className='map-point-dttracker'>{formatDate(new Date(row.dtTracker))}</p>
    {isAdminRole(session.user.role) && !row.machine.id ? <>
      <p className='main-btn' onClick={() => openModal((props) => MachineCreateWizard({
        ...props, services, session, machine: {
          imei: row.imei,
        }, onDone: (id: string) => {
          navigate(`/communal/machine/${id}`)
        }
      }))}>{session.translates.for('map.add_btn')}</p>

      <p className='main-btn' onClick={() => openModal((props) => MainDialogPopup({
        ...props,
        key: 'elements.dialog.remove_device',
        onOK: () => {
          services.session.fetchWithToken(`/api/communal/device/byimei/${row.imei}/delete`, {
            method: 'DELETE'
          }).then(() => {
            removeFromMap(row.imei)
            props.onClose()
          })
        }
      }))}>{session.translates.for('map.remove_btn')}</p>
    </> : null}

    {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
  </div>
}

const styleString = (style: any) => (
  Object.entries(style).map(([k, v]) => `${k}:${v}`).join(';')
)

export const mapStatusGroups = ['active', 'fire', 'inactive', 'new'] as const
export type MapStatusGroup = typeof mapStatusGroups[number]

export function mapStatus(row: MapRow, now = Date.now()): MapStatusGroup {
  if (!row.machine.id) return 'new'
  const timestamp = new Date(row.dtTracker).getTime()
  if (!Number.isFinite(timestamp) || timestamp > now || now - timestamp > activeTime) return 'inactive'
  return row.status.fire === true ? 'fire' : 'active'
}

export function vehicleCSS(row: MapRow): string {
  let css: any = {
    width: vehicleIconSize + 'px',
    height: vehicleIconSize + 'px',
    transform: `rotate(${row.angle + 45}deg)`,
    'transform-origin': 'center'
  }
  return styleString(css)
}

export function vehicleClass(row: MapRow, now = Date.now()): string {
  return `map-point-vehicle-${mapStatus(row, now)}`
}

export function vehicleElement(row: MapRow, now = Date.now()): string {
  return `<div class="${vehicleClass(row, now)}">`
    + (row.angle ?
      `<img src="/public/img/gps/vehicle_arrow.png" style="${vehicleCSS(row)}" />`
      : `<img src="/public/img/gps/map_point.png" style="${vehicleCSS(row)}" />`) +
    `</div>`
}

export function vehicleObject(row: MapRow, now = Date.now()): object {
  const halfVehicleIconSize = vehicleIconSize / 2
  return {
    className: 'car-icon',
    html: vehicleElement(row, now),
    iconSize: [vehicleIconSize, vehicleIconSize],
    iconAnchor: [halfVehicleIconSize, halfVehicleIconSize],
  }
}

function StatusMapViewport({ rows }: { rows: MapRow[] }) {
  const map = useMap()
  const initialRows = useRef(rows)

  // Fit only on mount; filters, polling and status ageing preserve the viewport.
  useEffect(() => {
    if (initialRows.current.length > 0) {
      map.fitBounds(L.latLngBounds(initialRows.current.map(row => L.latLng(row.lat, row.lng))), {
        padding: [24, 24], maxZoom: 13,
      })
    }
  }, [map])

  // The dashboard's animated side panels change the available map width.
  useEffect(() => {
    // Keep the geographic center and zoom when the panel changes map height.
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }))
    observer.observe(map.getContainer())
    return () => observer.disconnect()
  }, [map])

  return null
}

export const MapComponent = ({ fetchUrl, showStatusFilters = false, statusFiltersLayout = 'collapsible-bottom', children }: {
  fetchUrl: string
  showStatusFilters?: boolean
  statusFiltersLayout?: 'collapsible-bottom' | 'static-top'
  children?: React.ReactNode
}) => {
  const { services, session, translates } = useAppContext()
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const [enabled, setEnabled] = useState<MapStatusGroup[]>([...mapStatusGroups])
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const filtersPanelId = useId()
  const [, refreshStatuses] = useState(0)
  const now = Date.now()

  useEffect(() => {
    if (!showStatusFilters) return
    const timer = setInterval(() => refreshStatuses(tick => tick + 1), 2000)
    return () => clearInterval(timer)
  }, [showStatusFilters])

  async function removeFromMap(imei: string) {
    setMap(current => current && {
      ...current, rows: current.rows?.filter(row => row.imei !== imei) ?? null,
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    let disposed = false
    let timer: ReturnType<typeof setTimeout>
    let fromDate = ''
    const controller = new AbortController()

    async function refresh() {
      try {
        const response = await services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate}`, {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Unable to load map')
        const data: MapResponse = await response.json()
        if (!disposed) {
          setMap(current => current === undefined ? data : mergeMapResponses(current, data))
          if (data.rows?.length) fromDate = new Date(data.time).toISOString()
        }
      } catch (error) {
        if (!disposed) console.error(error)
      } finally {
        if (!disposed) timer = setTimeout(refresh, 2000)
      }
    }

    void refresh()
    return () => {
      disposed = true
      clearTimeout(timer)
      controller.abort()
    }
  }, [services, fetchUrl])

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }

    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const classifiedRows = (map.rows ?? []).map(row => ({ row, group: mapStatus(row, now) }))
  const counts = Object.fromEntries(mapStatusGroups.map(group => [group, 0])) as Record<MapStatusGroup, number>
  classifiedRows.forEach(({ group }) => { counts[group]++ })
  const visibleRows = classifiedRows.filter(({ group }) => !showStatusFilters || enabled.includes(group)).map(({ row }) => row)
  const t = (key: string) => (translates ?? session?.translates)?.for(`map.filters.${key}`)
  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  const canvas = <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
    <TileLayer
      attribution='evoltag.pl'
      url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    />
    {showStatusFilters ? <StatusMapViewport rows={visibleRows} /> : null}
    {visibleRows.map((row) => (
      <Marker
        key={row.imei}
        position={row}
        icon={L.divIcon(vehicleObject(row, now))} >
        <Popup>
          <MapPopupBody row={row} removeFromMap={removeFromMap} />
        </Popup>
      </Marker>
    ))}

    {myposition ? <Marker
      key='my-location'
      position={myposition}
      icon={L.divIcon({
        className: 'car-icon',
        html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px;" />`,
        iconSize: [32, 32], // Adjust size as needed
        iconAnchor: [16, 16], // Anchor point
      })}></Marker> : null}
  </MapContainer>

  if (!showStatusFilters && !children) return canvas

  const statusFilters = <fieldset className='map-filter-legend' disabled={statusFiltersLayout === 'collapsible-bottom' && !filtersExpanded}>
    <legend>{t('legend')}</legend>
    {mapStatusGroups.map(group => <label key={group} className='map-filter-option'>
      <input type='checkbox' checked={enabled.includes(group)} onChange={() => {
        setEnabled(current => current.includes(group)
          ? current.filter(item => item !== group)
          : [...current, group])
      }} />
      <span className={`map-filter-symbol map-point-vehicle-${group}`} aria-hidden='true'>
        <img src='/public/img/gps/vehicle_arrow.png' alt='' />
      </span>
      <span>{t(group)} ({counts[group]})</span>
    </label>)}
  </fieldset>

  if (showStatusFilters && statusFiltersLayout === 'static-top') {
    return <div className='status-map-view status-map-view-static-top'>
      {statusFilters}
      {visibleRows.length === 0 ? <p className='status-map-empty' role='status'>{t('empty')}</p> : null}
      <div className='status-map-canvas'>
        {canvas}{children}
      </div>
    </div>
  }

  return <div className='status-map-view'>
    <div className='status-map-canvas'>
      {canvas}{children}
      {showStatusFilters ? <button
        type='button'
        className='status-map-filter-toggle'
        aria-expanded={filtersExpanded}
        aria-controls={filtersPanelId}
        aria-label={t(filtersExpanded ? 'hide' : 'show')}
        title={t(filtersExpanded ? 'hide' : 'show')}
        onClick={() => setFiltersExpanded(expanded => !expanded)}>
        <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2'
          aria-hidden='true'>
          <path d={filtersExpanded ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6'} />
        </svg>
      </button> : null}
    </div>
    {showStatusFilters ? <div id={filtersPanelId}
      className={`status-map-panel${filtersExpanded ? '' : ' status-map-panel-collapsed'}`}
      aria-hidden={!filtersExpanded}>
      <div className='status-map-panel-content'>
        {visibleRows.length === 0 ? <p className='status-map-empty' role='status'>{t('empty')}</p> : null}
        {statusFilters}
      </div>
    </div> : null}
  </div>
}

export default MapComponent
